import { FC, useState } from "react";
import { Collapse, Link, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

interface Props {
	text?: string;
}

/**
 * FormFieldHint renders a collapsible element that shows a text.
 * This component is typically used in conjunction with Form* components.
 *
 * @param text the text to be rendered
 * @returns    a UI element that shows a helper text, with "show more" and "show less"
 *             buttons when appropriate
 */
const FormFieldHint: FC<Props> = ({ text }) => {
	const { t } = useTranslation();
	const words = (text ?? "").split(" ");
	const [collapsed, setCollapsed] = useState<boolean>(true);

	if (!text) {
		return <></>;
	}

	// No need to return a collapsible element.
	if (words.length <= 20) {
		return <>{text.trim()}</>;
	}

	let short = words.slice(0, 20).join(" ").trim();
	short = short.endsWith(".") ? `${short}..` : `${short}...`;

	const toggleCollapse = () => {
		setCollapsed(!collapsed);
	};

	return (
		<>
			<Collapse in={collapsed}>
				<Typography variant="caption">{short}</Typography>
				<Link pl={1} href="#" onClick={toggleCollapse} flexShrink={0}>
					[ {t("show more")} ]
				</Link>
			</Collapse>
			<Collapse in={!collapsed}>
				<Typography variant="caption">{text}</Typography>
				<Link pl={1} href="#" onClick={toggleCollapse} flexShrink={0}>
					[ {t("show less")} ]
				</Link>
			</Collapse>
		</>
	);
};

export default FormFieldHint;
